import * as React from 'react';
import { Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandItem,
  CommandGroup,
} from '@/components/ui/command';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { useTranslation } from 'react-i18next';

export interface EntitlementOption {
  module: string;
  tier: string;
}

interface EntitlementsComboboxProps {
  options: EntitlementOption[];
  selected: EntitlementOption[];
  onChange: (selected: EntitlementOption[]) => void;
  disabled?: boolean;
}

export default function EntitlementsCombobox({
  options,
  selected,
  onChange,
  disabled,
}: EntitlementsComboboxProps) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);

  const toggle = (opt: EntitlementOption) => {
    const exists = selected.some((s) => s.module === opt.module && s.tier === opt.tier);
    if (exists) {
      onChange(selected.filter((s) => !(s.module === opt.module && s.tier === opt.tier)));
    } else {
      onChange([...selected, opt]);
    }
  };

  const remove = (opt: EntitlementOption) => {
    onChange(selected.filter((s) => !(s.module === opt.module && s.tier === opt.tier)));
  };

  return (
    <div className="space-y-2">
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selected.map((s) => (
            <Badge
              key={`${s.module}-${s.tier}`}
              variant="secondary"
              className="flex items-center gap-1"
            >
              {s.module} — {s.tier}
              <button
                type="button"
                onClick={() => remove(s)}
                className="ml-1 rounded-full p-0.5 hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={t('licenses.entitlements.removeAria', { module: s.module })}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
      <Popover open={open && !disabled} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className="w-full justify-between font-normal"
          >
            {t('licenses.entitlements.selectPlaceholder')}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0" align="start">
          <Command>
            <CommandInput placeholder={t('licenses.entitlements.searchPlaceholder')} />
            <CommandList>
              <CommandEmpty>{t('licenses.entitlements.empty')}</CommandEmpty>
              <CommandGroup>
                {options.map((opt) => {
                  const isSelected = selected.some(
                    (s) => s.module === opt.module && s.tier === opt.tier
                  );
                  return (
                    <CommandItem key={`${opt.module}-${opt.tier}`} onSelect={() => toggle(opt)}>
                      <Check
                        className={cn('mr-2 h-4 w-4', isSelected ? 'opacity-100' : 'opacity-0')}
                      />
                      {opt.module} — {opt.tier}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
